import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalCredentialsPage() {
  const [portals, setPortals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', notes: '', enabled: true });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchPortals = async () => {
    const res = await fetch(`${API}/api/portal-credentials/portals`, { credentials: 'include' });
    if (res.ok) setPortals(await res.json());
    setLoading(false);
  };

  useEffect(() => { fetchPortals(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({ username: p.username || '', password: '', notes: '', enabled: p.enabled !== false });
  };

  const save = async () => {
    if (!editing) return;
    if (!form.username || !form.password) { setMsg('Usuario y contrasena son requeridos'); return; }
    const res = await fetch(`${API}/api/portal-credentials`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        portal: editing.key,
        label: editing.name,
        username: form.username,
        password: form.password,
        portal_url: editing.url,
        enabled: form.enabled,
        notes: form.notes,
      })
    });
    if (res.ok) {
      setMsg(`Credenciales guardadas para ${editing.name}`);
      setEditing(null);
      fetchPortals();
    } else {
      const err = await res.json().catch(() => ({}));
      setMsg(err.detail || 'Error al guardar');
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const remove = async (key, name) => {
    if (!window.confirm(`Eliminar credenciales de ${name}?`)) return;
    const res = await fetch(`${API}/api/portal-credentials/${key}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { setMsg(`Eliminado: ${name}`); fetchPortals(); }
    setTimeout(() => setMsg(''), 3000);
  };

  const toggle = async (key) => {
    await fetch(`${API}/api/portal-credentials/${key}/toggle`, { method: 'POST', credentials: 'include' });
    fetchPortals();
  };

  return (
    <div data-testid="portal-creds-page" className="space-y-6">
      <div className="border-b border-slate-700 pb-4">
        <h1 className="text-3xl font-bold text-white tracking-tight">Credenciales de Portales B2B</h1>
        <p className="text-slate-300 text-sm mt-1">Accesos a portales de licitaciones (BidNet, DemandStar, SAM.gov, etc.) para la busqueda automatica</p>
      </div>

      {msg && <div data-testid="pc-msg" className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      {loading ? <p className="text-slate-400 text-sm">Cargando...</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {portals.map(p => (
            <Card key={p.key} data-testid={`portal-${p.key}`} className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-base">{p.name}</CardTitle>
                    <p className="text-slate-400 text-xs mt-1">{p.description}</p>
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-blue-400 text-xs hover:underline">{p.url}</a>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {p.requires_login ? <span className="text-xs bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded">Login requerido</span> : <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">Publico</span>}
                    {p.has_credentials && (p.enabled ? <span className="text-xs bg-green-900/40 text-green-300 px-2 py-0.5 rounded">Activo</span> : <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">Pausado</span>)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {p.has_credentials ? (
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">Usuario: <span className="text-white font-mono">{p.username}</span></p>
                    {p.updated_at && <p className="text-slate-500 text-xs">Actualizado: {new Date(p.updated_at).toLocaleString('es-ES')}</p>}
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" data-testid={`edit-${p.key}`} onClick={() => openEdit(p)}>Editar</Button>
                      <Button size="sm" variant="outline" data-testid={`toggle-${p.key}`} onClick={() => toggle(p.key)}>{p.enabled ? 'Pausar' : 'Activar'}</Button>
                      <Button size="sm" variant="destructive" data-testid={`delete-${p.key}`} onClick={() => remove(p.key, p.name)}>Eliminar</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" data-testid={`add-${p.key}`} onClick={() => openEdit(p)} className="bg-blue-600 hover:bg-blue-700">
                    {p.requires_login ? 'Agregar credenciales' : 'Configurar acceso (opcional)'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <Card className="bg-slate-800 border-slate-700 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white">Credenciales: {editing.name}</CardTitle>
              <p className="text-slate-400 text-xs">Se almacenan cifradas con Fernet. Solo administradores.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Usuario / Email</label>
                <Input data-testid="pc-username" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="usuario@empresa.com" />
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Contrasena</label>
                <Input data-testid="pc-password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editing.has_credentials ? 'Dejar vacio para mantener la actual' : ''} />
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Notas (opcional)</label>
                <Input data-testid="pc-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Vence: MM/YY, Plan: Pro, etc." />
              </div>
              <label className="flex items-center gap-2 text-slate-300 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                Activar para busqueda automatica
              </label>
              <div className="flex gap-2 pt-2">
                <Button data-testid="pc-save" onClick={save} className="flex-1 bg-blue-600 hover:bg-blue-700">Guardar</Button>
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
